import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Ticket, Plus, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { BottomNav } from "../components/BottomNav";
import SupportModal from "../components/SupportModal";

const STATUS = {
  open:        { en: "Open",        es: "Abierto",        color: "rgba(245,158,11,0.5)",  text: "#FBBF24" },
  in_progress: { en: "In Progress", es: "En proceso",     color: "rgba(56,189,248,0.5)",  text: "#7DD3FC" },
  resolved:    { en: "Resolved",    es: "Resuelto",       color: "rgba(52,211,153,0.5)",  text: "#6EE7B7" },
};
const CATEGORY = {
  transportation: "Transportation",
  vip_concierge: "VIP / Concierge",
  app_problem: "App Problem",
  account: "Account",
  business_vendor: "Business / Vendor",
  other: "Other",
};

function fmt(iso) {
  try { return new Date(iso).toLocaleString("en-US", { year: "2-digit", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso || ""; }
}

function StatusBadge({ s }) {
  const meta = STATUS[s] || STATUS.open;
  return (
    <span
      data-testid={`ticket-status-${s}`}
      className="inline-block uppercase tracking-widest border"
      style={{
        fontSize: 10,
        padding: "3px 8px",
        borderColor: meta.color,
        color: meta.text,
        borderRadius: 4,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {meta.en}
    </span>
  );
}

export function MyTicketsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);

  useEffect(() => {
    api.get("/support/my-tickets")
      .then((r) => setData(r.data))
      .catch((e) => {
        if (e.response?.status === 401) navigate("/login");
        else setErr(e.response?.data?.detail || e.message);
      });
  }, [navigate]);

  return (
    <main data-testid="my-tickets-page" className="min-h-screen pb-32 bg-background">
      <header className="px-5 pt-6 pb-3 flex items-center gap-3">
        <button type="button" onClick={() => navigate(-1)} data-testid="my-tickets-back" className="btn-ghost p-2" aria-label="Back">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        </button>
        <div>
          <div className="eyebrow">Habano</div>
          <h1 className="font-display text-3xl mt-1 text-foreground">My Tickets</h1>
        </div>
      </header>

      <section className="px-5 mt-3">
        <button
          type="button"
          data-testid="my-tickets-open-request"
          onClick={() => setSupportOpen(true)}
          className="btn-outline w-full justify-center"
          style={{ minHeight: 52 }}
        >
          <Plus className="w-4 h-4" strokeWidth={1.5} /> Open a Support Request
        </button>
      </section>

      <section className="px-5 mt-5">
        {err && <div className="text-amber-300 text-[12px]">{err}</div>}
        {!data ? (
          <div className="text-foreground/55 text-sm">Loading…</div>
        ) : data.tickets.length === 0 ? (
          <div data-testid="my-tickets-empty" className="lux-card p-5 text-center text-foreground/70 text-sm">
            No support requests yet. If you need help tap <span className="text-gold">Open a Support Request</span>.
          </div>
        ) : (
          <ul className="space-y-3">
            {data.tickets.map((t) => (
              <li key={t.ticket_id}>
                <Link
                  to={`/support/tickets/${t.ticket_id}`}
                  data-testid={`my-tickets-row-${t.ticket_id}`}
                  className="lux-card p-4 block hover:bg-white/[0.03] transition-colors"
                  style={{ minHeight: 64 }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-mono text-gold text-[13px]">{t.ticket_id}</div>
                    <StatusBadge s={t.status} />
                  </div>
                  <div className="mt-1.5 text-foreground" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}>
                    {CATEGORY[t.category] || t.category}
                  </div>
                  <div className="mt-0.5 text-[11px] text-foreground/55">Submitted {fmt(t.created_at)}</div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <SupportModal open={supportOpen} onOpenChange={setSupportOpen} />
      <BottomNav />
    </main>
  );
}

export function TicketDetailPage() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [err, setErr] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get(`/support/tickets/${ticketId}`)
      .then((r) => setTicket(r.data))
      .catch((e) => {
        if (e.response?.status === 401) navigate("/login");
        else setErr(e.response?.data?.detail || e.message);
      });
  };
  useEffect(load, [ticketId, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitReply = async () => {
    const msg = reply.trim();
    if (!msg) return;
    setSending(true);
    try {
      const { data } = await api.post(`/support/tickets/${ticketId}/reply`, { message: msg });
      setTicket(data);
      setReply("");
      toast.success("Your message has been added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send — try again");
    } finally {
      setSending(false);
    }
  };

  const canReply = ticket && (ticket.status === "open" || ticket.status === "in_progress");
  const thread = ticket ? buildThread(ticket) : [];

  return (
    <main data-testid="ticket-detail-page" className="min-h-screen pb-32 bg-background">
      <header className="px-5 pt-6 pb-3 flex items-center gap-3">
        <button type="button" onClick={() => navigate(-1)} data-testid="ticket-detail-back" className="btn-ghost p-2" aria-label="Back">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        </button>
        <div>
          <div className="eyebrow">Support request</div>
          <h1 className="font-display text-3xl mt-1 text-foreground" data-testid="ticket-detail-id">
            {ticket?.ticket_id || ticketId}
          </h1>
        </div>
      </header>

      <section className="px-5 mt-3">
        {err && <div className="text-amber-300 text-[12px]" data-testid="ticket-detail-error">{err}</div>}
        {!ticket ? (
          <div className="text-foreground/55 text-sm">Loading…</div>
        ) : (
          <>
            <article className="lux-card p-5" style={{ background: "#171717" }}>
              <div className="flex items-center justify-between mb-3">
                <div className="eyebrow">{CATEGORY[ticket.category] || ticket.category}</div>
                <StatusBadge s={ticket.status} />
              </div>

              <div className="space-y-3" data-testid="ticket-detail-thread">
                {thread.map((m) => (
                  <ThreadMessage key={m.id} m={m} />
                ))}
              </div>

              {ticket.screenshot && (
                <div className="mt-4">
                  <div className="eyebrow text-gold mb-1">Screenshot</div>
                  <img src={ticket.screenshot} alt="" className="max-w-full border border-[rgba(232,184,75,0.25)] rounded" data-testid="ticket-detail-screenshot" />
                </div>
              )}

              <div className="mt-6 grid grid-cols-2 gap-3 text-[11px] text-foreground/55">
                <div>
                  <div className="uppercase tracking-widest">Submitted</div>
                  <div className="text-foreground/85 font-mono mt-0.5">{fmt(ticket.created_at)}</div>
                </div>
                <div>
                  <div className="uppercase tracking-widest">Last updated</div>
                  <div className="text-foreground/85 font-mono mt-0.5">{fmt(ticket.updated_at)}</div>
                </div>
              </div>

              {ticket.status === "resolved" && (
                <button
                  type="button"
                  data-testid="ticket-detail-submit-another"
                  onClick={() => setSupportOpen(true)}
                  className="btn-gold w-full justify-center mt-6"
                  style={{ minHeight: 52 }}
                >
                  <Ticket className="w-4 h-4" strokeWidth={1.6} /> Submit another request
                </button>
              )}
            </article>

            {canReply && (
              <article
                className="lux-card p-5 mt-4"
                data-testid="ticket-detail-reply-section"
                style={{ background: "#171717" }}
              >
                <div className="eyebrow mb-2">Add a follow-up message</div>
                <textarea
                  data-testid="ticket-detail-reply-input"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Add more details or a follow-up message"
                  rows={4}
                  className="w-full"
                  style={{
                    background: "#1D1D1D",
                    border: "1px solid rgba(232,184,75,0.25)",
                    borderRadius: 10,
                    padding: 12,
                    color: "#F5F1E8",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 16,
                    minHeight: 110,
                    resize: "vertical",
                  }}
                />
                <button
                  type="button"
                  data-testid="ticket-detail-send-reply"
                  onClick={submitReply}
                  disabled={sending || !reply.trim()}
                  className="btn-gold w-full justify-center mt-3"
                  style={{ minHeight: 52, opacity: sending || !reply.trim() ? 0.55 : 1 }}
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.6} />}
                  Send Follow-up
                </button>
              </article>
            )}
          </>
        )}
      </section>

      <SupportModal open={supportOpen} onOpenChange={setSupportOpen} />
      <BottomNav />
    </main>
  );
}

/**
 * Build a chronologically-ordered list of thread messages from a ticket payload.
 * The initial ticket.message is treated as the first user message; ticket.notes
 * (already scrubbed by the API) supply the rest.
 */
function buildThread(ticket) {
  const items = [
    {
      id: `${ticket.ticket_id}-initial`,
      note: ticket.message,
      from_user: true,
      by_name: "You",
      at: ticket.created_at,
    },
    ...(ticket.notes || []).map((n) => ({
      id: n.id || n.at,
      note: n.note,
      from_user: !!n.from_user,
      by_name: n.from_user ? "You" : "Habano team",
      at: n.at,
      screenshot: n.screenshot,
    })),
  ];
  items.sort((a, b) => (a.at || "").localeCompare(b.at || ""));
  return items;
}

function ThreadMessage({ m }) {
  const isUser = m.from_user;
  return (
    <div
      data-testid={`ticket-thread-${isUser ? "user" : "admin"}-${m.id}`}
      className="px-3 py-2.5"
      style={{
        background: isUser ? "#1D1D1D" : "rgba(232,184,75,0.06)",
        border: isUser ? "0.5px solid rgba(232,184,75,0.12)" : "0.5px solid rgba(232,184,75,0.35)",
        borderLeft: isUser ? "0.5px solid rgba(232,184,75,0.12)" : "3px solid #E8B84B",
        borderRadius: 8,
        color: "#F5F1E8",
      }}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span
          className="uppercase tracking-widest"
          style={{ color: isUser ? "#B0AAA3" : "#E8B84B", fontSize: 10, fontFamily: "'DM Sans', sans-serif" }}
        >
          {isUser ? m.by_name || "You" : "Message from Habano team"}
        </span>
        <span style={{ color: "rgba(245,241,232,0.45)", fontSize: 10, fontFamily: "ui-monospace, monospace" }}>
          {fmt(m.at)}
        </span>
      </div>
      <div className="whitespace-pre-wrap" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, lineHeight: 1.5 }}>
        {m.note}
      </div>
      {m.screenshot && (
        <img src={m.screenshot} alt="" className="mt-2 max-w-full border border-[rgba(232,184,75,0.25)] rounded" />
      )}
    </div>
  );
}
